import { stringToNumber } from '@marmon/shared/types/common';
import { Buffer } from 'buffer';

/**
 * Generates a segment sequence to save an encoding in the database for quick association. We take all of
 * the segments in the analysis and assign them a boolean value based on if they should be active in the
 * calculations or not. Then we organize them in an array only including the index of each segment and then
 * encoding it before returning it. This is to ensure each calculation is only saved once and each segmentSequence
 * should be unique. The value can be too long to be a true id so we need to query against the entire collection
 * to retrieve it.
 *
 * @param segmentMap a map of all the segments in analysis mapped with if they are active or not for the calculation
 * @returns
 */
export function encodeSegmentSequence(
	segmentMap: Map<string, boolean>,
): string | null {
	const sequence: number[] = [];
	Array.from(segmentMap)
		.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
		.forEach(([, value], i) => {
			if (value) sequence.push(i);
		});

	// return null for the sequence if every segment is enabled.
	if (sequence.length === segmentMap.size) return null;

	const stringified = JSON.stringify(sequence);
	return Buffer.from(stringified).toString('base64');
}

export function decodeSegmentSequence(
	segmentSequence: string,
	segments: string[],
): Map<string, boolean> {
	segments = segments.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
	const decoded = Buffer.from(segmentSequence, 'base64').toString('ascii');
	const parsed = JSON.parse(decoded) as unknown[]; // should be number[], but we need type checks to be sure

	if (!Array.isArray(parsed))
		throw Error('Decoded sequence had unexpected value');

	const formatted = parsed.map((p) => {
		if (typeof p === 'number') return p;
		if (typeof p === 'string') {
			return stringToNumber(p);
		}
		throw Error(`Parsed sequence had unexpected value type: ${typeof p}`);
	});

	const segmentMap = new Map<string, boolean>();
	segments.forEach((segment, i) => {
		// the segment index needs to be included in the sequence.
		segmentMap.set(segment, formatted.includes(i));
	});

	return segmentMap;
}

/**
 * Whether a raw CSV row belongs to the active segment set.
 * `null` segments means every row is included (the default calculation).
 */
export function rawInvoiceMatchesSegments(
	row: { Segment?: string },
	segments: Map<string, boolean> | null,
): boolean {
	if (!segments) return true;
	const segment = row.Segment?.trim();
	return Boolean(segment && segments.get(segment));
}
